// AGC: useCustomList - Shared hook to load user-defined custom list items
// AGC: Dual-persisted: localStorage for instant display + Firestore for cross-device sync
// AGC: On mount: reads localStorage first, then Firestore (overwrites if Firestore has newer data)
// AGC: On write: saves to both localStorage AND Firestore

import { useState, useEffect, useCallback, useRef } from 'react'
import { doc, getDoc, setDoc } from 'firebase/firestore'
import { db } from '../lib/firebase'
import { useFarms } from '../context/FarmContext'
import { useAuth } from './useAuth'
import type { CustomListItem } from '../components/inventory/ManageCategoriesUnitsModal'

export function useCustomList(storageKeyPrefix: string): CustomListItem[] {
  const { currentFarm } = useFarms()
  const { user } = useAuth()
  const farmId = currentFarm?.id || 'default'
  const [items, setItems] = useState<CustomListItem[]>([])
  const hasSyncedRef = useRef(false)

  useEffect(() => {
    hasSyncedRef.current = false
  }, [storageKeyPrefix, farmId])

  useEffect(() => {
    const localKey = `${storageKeyPrefix}_${farmId}`

    // AGC: 1. Load from localStorage first (instant)
    const loadLocal = () => {
      try {
        const raw = localStorage.getItem(localKey)
        if (raw) {
          const parsed = JSON.parse(raw) as CustomListItem[]
          if (Array.isArray(parsed)) { setItems(parsed); return parsed }
        }
      } catch {}
      setItems([])
      return [] as CustomListItem[]
    }
    const localItems = loadLocal()

    // AGC: 2. Then sync from Firestore (cross-device)
    if (user?.uid && !hasSyncedRef.current) {
      hasSyncedRef.current = true
      const firestoreKey = `${storageKeyPrefix}_${farmId}`.replace(/[./]/g, '_')
      getDoc(doc(db, 'users', user.uid, 'preferences', firestoreKey)).then(snap => {
        const data = snap.data()
        if (data?.items && Array.isArray(data.items) && data.items.length > 0) {
          // AGC: Firestore has data — use it if localStorage was empty or different
          if (localItems.length === 0 || JSON.stringify(localItems) !== JSON.stringify(data.items)) {
            setItems(data.items)
            localStorage.setItem(localKey, JSON.stringify(data.items))
          }
        } else if (localItems.length > 0) {
          // AGC: Firestore empty but localStorage has data — push up to Firestore
          setDoc(doc(db, 'users', user.uid!, 'preferences', firestoreKey), { items: localItems }, { merge: true }).catch(() => {})
        }
      }).catch(() => {})
    }

    const onStorage = (e: StorageEvent) => {
      if (e.key === localKey) loadLocal()
    }
    window.addEventListener('storage', onStorage)
    return () => window.removeEventListener('storage', onStorage)
  }, [storageKeyPrefix, farmId, user?.uid])

  return items
}

/**
 * AGC: Persist a custom list to both localStorage and Firestore
 */
export function persistCustomList(
  storageKeyPrefix: string,
  farmId: string,
  items: CustomListItem[],
  userId?: string,
) {
  const localKey = `${storageKeyPrefix}_${farmId}`
  try {
    localStorage.setItem(localKey, JSON.stringify(items))
    window.dispatchEvent(new StorageEvent('storage', { key: localKey, newValue: JSON.stringify(items) }))
  } catch {}

  // AGC: Write to Firestore for cross-device sync
  if (userId) {
    const firestoreKey = localKey.replace(/[./]/g, '_')
    setDoc(doc(db, 'users', userId, 'preferences', firestoreKey), { items }, { merge: true }).catch(() => {})
  }
}
